import {
  getDateWithTimeZone,
  setFormatDateToView
} from '@/solaris-lens-common-lib/src/utils/dates';

const getBrowserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Formats a UTC date for Last Update using the same converter as calendar END.
 * @param {Date | string | number} date - UTC date or ISO string.
 * @param {string} [timezone] - Turbine IANA timezone; falls back to the browser.
 * @returns {string} Last Update string in the resolved timezone.
 */
export const formatLastUpdate = (
  date: Date | string | number,
  timezone?: string
): string => {
  const tz = timezone || getBrowserTimezone();
  try {
    return setFormatDateToView(
      getDateWithTimeZone({
        date: new Date(date),
        timezone: tz
      })
    );
  } catch {
    return setFormatDateToView(
      getDateWithTimeZone({
        date: new Date(date),
        timezone: getBrowserTimezone()
      })
    );
  }
};

export type LastUpdateDisplayParams = {
  lastUpdateTime: string;
  hasWebsocketData: boolean;
  sentAt?: string;
  timezone?: string;
  powerStatus?: string;
};

/**
 * Picks the Last Update string: REST sentAt in turbine timezone until the
 * websocket has delivered a timestamp (and always for offline turbines).
 * @param {LastUpdateDisplayParams} params - Context time, socket flag, and REST status.
 * @returns {string} Display string for the Last Update label.
 */
export const getLastUpdateDisplayTime = ({
  lastUpdateTime,
  hasWebsocketData,
  sentAt,
  timezone,
  powerStatus
}: LastUpdateDisplayParams): string => {
  const shouldUseRestTimestamp =
    !hasWebsocketData || powerStatus === 'offline';

  if (shouldUseRestTimestamp && (sentAt || timezone)) {
    return formatLastUpdate(sentAt || new Date(), timezone);
  }

  return lastUpdateTime;
};
